// ============================================================
//  src/pages/auth/ForgotPasswordPage.js
//  Three steps in one page:
//    1) request — enter email, get OTP
//    2) verify  — enter OTP, get reset_token
//    3) reset   — enter new password
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { forgotPasswordReq, forgotPasswordVerify, forgotPasswordReset } from '../../api/client';
import { getError } from '../../utils/helpers';
import AuthShell, { AuthAlert, PasswordStrength } from './AuthShell';

const RESEND_SECONDS = 60;

function Steps({ step }) {
  const items = ['Email', 'Code', 'New password'];
  return (
    <ol className="auth-steps" aria-label={`Step ${step} of 3`}>
      {items.map((label, i) => {
        const n = i + 1;
        const state = n < step ? 'done' : n === step ? 'current' : '';
        return (
          <li key={label} className={state}>
            <span className="auth-step-dot">{n < step ? <i className="fas fa-check" /> : n}</span>
            <span className="auth-step-label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

export default function ForgotPasswordPage() {
  const nav = useNavigate();
  const [step, setStep]         = useState(1);
  const [email, setEmail]       = useState('');
  const [digits, setDigits]     = useState(['', '', '', '', '', '']);
  const [resetToken, setRT]     = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [cooldown, setCooldown] = useState(0);
  const inputs = useRef([]);

  useEffect(() => {
    if (step === 2) inputs.current[0]?.focus();
  }, [step]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // ── OTP grid helpers ──
  const onDigit = (idx, val) => {
    const clean = val.replace(/\D/g, '');
    if (clean.length > 1) {           // autofill / fast typing dumps several digits at once
      const next = [...digits];
      for (let i = 0; i < clean.length && idx + i < 6; i++) next[idx + i] = clean[i];
      setDigits(next);
      inputs.current[Math.min(idx + clean.length, 5)]?.focus();
      return;
    }
    const v = clean.slice(0, 1);
    setDigits((p) => { const n = [...p]; n[idx] = v; return n; });
    if (v && idx < 5) inputs.current[idx + 1]?.focus();
  };
  const onKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) inputs.current[idx - 1]?.focus();
    if (e.key === 'ArrowLeft' && idx > 0) inputs.current[idx - 1]?.focus();
    if (e.key === 'ArrowRight' && idx < 5) inputs.current[idx + 1]?.focus();
  };
  const onPaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setDigits(next);
    inputs.current[Math.min(text.length, 5)]?.focus();
  };

  // ── Step 1: request OTP ──
  const requestCode = async () => {
    const { data } = await forgotPasswordReq({ email: email.trim() });
    if (!data?.success) throw new Error(data?.message || 'Could not send code.');
    toast.success(data?.message || 'Code sent.');
    setCooldown(RESEND_SECONDS);
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError('Enter the email on your account.'); return; }
    setLoading(true); setError('');
    try {
      await requestCode();
      setStep(2);
    } catch (err) {
      setError(err?.response ? getError(err) : err.message);
    } finally { setLoading(false); }
  };

  const resend = async () => {
    setError('');
    try { await requestCode(); setDigits(['', '', '', '', '', '']); inputs.current[0]?.focus(); }
    catch (err) { setError(err?.response ? getError(err) : err.message); }
  };

  // ── Step 2: verify OTP ──
  const submitVerify = async (e) => {
    e.preventDefault();
    const otp = digits.join('');
    if (otp.length !== 6) { setError('Enter the 6-digit code.'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await forgotPasswordVerify({ email: email.trim(), otp });
      if (!data?.success) { setError(data?.message || 'Invalid code.'); return; }
      setRT(data?.data?.reset_token || '');
      setStep(3);
    } catch (err) {
      setError(getError(err));
    } finally { setLoading(false); }
  };

  // ── Step 3: reset password ──
  const submitReset = async (e) => {
    e.preventDefault();
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true); setError('');
    try {
      const { data } = await forgotPasswordReset({
        email: email.trim(),
        reset_token: resetToken,
        password,
      });
      if (!data?.success) { setError(data?.message || 'Could not reset password.'); return; }
      toast.success('Password updated. Sign in with your new password.');
      nav('/login', { replace: true });
    } catch (err) {
      setError(getError(err));
    } finally { setLoading(false); }
  };

  const titles = {
    1: 'Forgot your password?',
    2: 'Check your email',
    3: 'Set a new password',
  };
  const subtitles = {
    1: 'No worries. Enter the email on your account and we’ll send you a 6-digit reset code.',
    2: <>We sent a 6-digit code to <b className="auth-em">{email}</b>. It expires in 15 minutes.</>,
    3: 'Choose a strong password you don’t use anywhere else.',
  };

  return (
    <AuthShell
      visual="secure"
      headline={<>Locked out? <em>We’ll get you back in.</em></>}
      sub="Reset your password in three quick steps. Your bots keep running the whole time."
      points={['Code sent to your account email', 'Codes expire after 15 minutes', 'Your bots and data stay untouched']}
      switchText="Remembered it?"
      switchTo="/login"
      switchLabel="Sign in"
      above={<Steps step={step} />}
      title={titles[step]}
      subtitle={subtitles[step]}
    >
      <AuthAlert>{error}</AuthAlert>

      {step === 1 && (
        <form onSubmit={submitRequest} noValidate>
          <div className="auth-field">
            <label htmlFor="fp-email">Email</label>
            <div className="auth-field-wrap">
              <i className="fas fa-envelope fi" />
              <input
                id="fp-email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                autoFocus
                required
              />
            </div>
          </div>
          <button type="submit" className="btn-auth" disabled={loading}>
            {loading
              ? <><span className="spinner" /> Sending code…</>
              : <>Send reset code <i className="fas fa-arrow-right" /></>}
          </button>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={submitVerify} noValidate>
          <label className="form-label">Verification code</label>
          <div className="otp-grid" onPaste={onPaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputs.current[i] = el)}
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                aria-label={`Digit ${i + 1}`}
                maxLength={i === 0 ? 6 : 1}
                value={d}
                className={d ? 'filled' : ''}
                onChange={(e) => onDigit(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                onFocus={(e) => e.target.select()}
              />
            ))}
          </div>
          <button type="submit" className="btn-auth" disabled={loading || digits.join('').length !== 6}>
            {loading
              ? <><span className="spinner" /> Verifying…</>
              : <>Verify code <i className="fas fa-arrow-right" /></>}
          </button>
          <div className="auth-resend">
            <span>Didn’t get it?</span>
            {cooldown > 0
              ? <span className="auth-resend-wait">Resend in {cooldown}s</span>
              : <button type="button" className="auth-text-btn" onClick={resend}>Resend code</button>}
            <span className="auth-legal-dot" />
            <button type="button" className="auth-text-btn muted" onClick={() => { setStep(1); setError(''); setDigits(['', '', '', '', '', '']); }}>
              Change email
            </button>
          </div>
        </form>
      )}

      {step === 3 && (
        <form onSubmit={submitReset} noValidate>
          <div className="auth-field">
            <label htmlFor="fp-pw">New password</label>
            <div className="auth-field-wrap">
              <i className="fas fa-lock fi" />
              <input
                id="fp-pw"
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                autoFocus
                required
              />
              <button
                type="button"
                className="pw-toggle"
                onClick={() => setShowPwd((v) => !v)}
                aria-label={showPwd ? 'Hide password' : 'Show password'}
              >
                <i className={`fas ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
              </button>
            </div>
            <PasswordStrength value={password} />
          </div>
          <button type="submit" className="btn-auth" disabled={loading}>
            {loading
              ? <><span className="spinner" /> Updating…</>
              : <>Update password <i className="fas fa-check" /></>}
          </button>
        </form>
      )}

      <div className="auth-bottom">
        <Link to="/login" className="auth-back"><i className="fas fa-arrow-left" /> Back to sign in</Link>
      </div>
    </AuthShell>
  );
}
